import React, { useState } from 'react';
import { Card, Form, Input, Button, Alert, Typography, message } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const { Text: AntText } = Typography;

/**
 * 登录页：
 *  - 正常登录成功 → 跳到目标页（默认 /）
 *  - 后端返回 require_password_change=true → 跳到 /change-password
 *  - 失败次数超限（被锁定 15 分钟）→ 后端返回 429，前端只显示后端文案
 */
export default function Login() {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const { user, login, pendingChange } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 已经登录了 → 跳主页
  if (user) {
    const dest = location.state?.from || '/';
    return <Navigate to={dest} replace />;
  }
  // 在改密握手态（持有 changeToken 但未拿到正式 token）→ 强制去改密页
  if (pendingChange && pendingChange.changeToken) {
    return <Navigate to="/change-password" replace />;
  }

  const onFinish = async (values) => {
    setErrorMsg('');
    setLoading(true);
    try {
      await login(values.username, values.password);
      message.success('登录成功');
      const dest = location.state?.from || '/';
      navigate(dest, { replace: true });
    } catch (e) {
      if (e?.code === 'PASSWORD_CHANGE_REQUIRED') {
        // 跳到改密页，并把当前用户名带过去方便显示
        navigate('/change-password', { state: { from: location.state?.from } });
        return;
      }
      setErrorMsg(e?.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Card
        title={
          <span>
            <SafetyCertificateOutlined style={{ marginRight: 8 }} />
            私募数据平台 · 登录
          </span>
        }
        className="login-card"
        styles={{ header: { textAlign: 'center', fontSize: 18 } }}
      >
        {errorMsg && <Alert type="error" message={errorMsg} style={{ marginBottom: 12 }} />}
        <Form name="login" onFinish={onFinish} autoComplete="off" size="large">
          <Form.Item name="username" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input prefix={<UserOutlined />} placeholder="用户名" autoComplete="username" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="密码" autoComplete="current-password" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              登 录
            </Button>
          </Form.Item>
        </Form>
        <AntText type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center', marginTop: 8 }}>
          仅授权账号可访问。连续 5 次密码错误将锁定 15 分钟。
        </AntText>
      </Card>
    </div>
  );
}